/**
 * 計助ダッシュボード: 長形式 gridData の行数・空行扱い
 * - 長形式表は最低行数を確保（空行パディング。仮想スクロールで描画）
 * - 空文字は数値として 0 にしない（Number('') === 0 対策）
 */

import type { DataRow } from '../stores/systems/keisuke/dashboardStore';

/** 空グリッド／少数データのときの最低表示行数（編集余白） */
export const KEISUKE_GRID_MIN_DISPLAY_ROWS = 12;
/** 実データ末尾に足す編集用空行（多すぎるとサンプル後に空行砂漠になる） */
export const KEISUKE_GRID_TRAILING_EMPTY_BUFFER = 2;

/** 長形式仮想表の表示行数目標（実データ＋バッファ、下限あり） */
export function longFormatDisplayRowTarget(dataRowCount: number): number {
  const n = Math.max(0, Math.floor(dataRowCount));
  return Math.max(KEISUKE_GRID_MIN_DISPLAY_ROWS, n + KEISUKE_GRID_TRAILING_EMPTY_BUFFER);
}

/**
 * セル値を有限の数値に変換。空文字・空白のみ・非数は null。
 * Number('') が 0 になるのを防ぐ。
 */
export function parseFiniteNumberFromCell(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (t === '') return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function isRowEmptyForHeaders(headers: string[], row: DataRow): boolean {
  return headers.every((h) => {
    const v = row[h];
    if (v === '' || v === undefined || v === null) return true;
    if (typeof v === 'string' && v.trim() === '') return true;
    return false;
  });
}

/** 解析結果帯・空ヒーロー切替用: 数値として解釈できるセルが1つでもあれば true */
export function gridDataHasNumericValues(headers: string[], data: DataRow[]): boolean {
  if (headers.length === 0 || data.length === 0) return false;
  return data.some((row) => headers.some((h) => parseFiniteNumberFromCell(row[h]) != null));
}

/**
 * 全ヘッダキーを持つ行に正規化し、行数を少なくとも minRows まで埋める。
 * 既に minRows 超なら行数は維持（切り詰めない）。
 */
export function padGridDataToMinRows(headers: string[], data: DataRow[], minRows: number): DataRow[] {
  const target = Math.max(minRows, data.length);
  const normalize = (row: DataRow): DataRow => {
    const r: DataRow = {};
    for (const h of headers) {
      const v = row[h];
      if (v === undefined || v === null) r[h] = '';
      else r[h] = v;
    }
    return r;
  };
  const emptyRow = (): DataRow => {
    const r: DataRow = {};
    for (const h of headers) r[h] = '';
    return r;
  };
  const out = data.map(normalize);
  while (out.length < target) out.push(emptyRow());
  return out;
}

/** 末尾の「全セル空」行だけを除いたコピー（.keisuke 保存用など） */
export function trimTrailingEmptyGridRows(headers: string[], data: DataRow[]): DataRow[] {
  let end = data.length;
  while (end > 0 && isRowEmptyForHeaders(headers, data[end - 1]!)) end -= 1;
  return data.slice(0, end);
}

/** ヘッダー列を揃えた行（パディングなし） */
function normalizeGridRowKeys(headers: string[], row: DataRow): DataRow {
  const r: DataRow = {};
  for (const h of headers) {
    const v = row[h];
    if (v === undefined || v === null) r[h] = '';
    else r[h] = v;
  }
  return r;
}

/**
 * ストアへ格納する gridData を正規化。
 * - 長形式仮想表: 末尾空行除去後に表示用最低行数までパディング
 * - ただし「行を追加」で意図的に伸ばした空行は raw.length を下限に残す
 *   （trim→固定バッファ再 pad だと追加が無操作に見える）
 * - コンパクト表（用量反応・t検定2列等）: パディングせず実データのみ
 */
export function normalizeGridDataForStore(
  headers: string[],
  raw: DataRow[],
  padForLongFormatVirtual: boolean
): DataRow[] {
  const trimmed = trimTrailingEmptyGridRows(headers, raw).map((row) =>
    normalizeGridRowKeys(headers, row)
  );
  if (padForLongFormatVirtual) {
    const target = Math.max(longFormatDisplayRowTarget(trimmed.length), raw.length);
    return padGridDataToMinRows(headers, trimmed, target);
  }
  return trimmed;
}
